import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  useMediaQuery,
  useTheme
} from "@material-ui/core";
import { X, BookOpen } from "lucide-react";

import AI_TOOLS_GUIDE from "./content";
import ChatBubble from "./ChatBubble";

/**
 * Modal "Guia das automacoes da IA" — exibe explicacao + exemplo de
 * conversa em bolhas para cada uma das 5 tools opt-in do Agente IA
 * (Transferir para atendente, Encerrar, Transferir para fila,
 * Etiquetar, Adicionar nota interna).
 *
 * UX:
 * - Desktop: Dialog com sidebar a esquerda (lista das tools) e
 *   conteudo a direita (selecionada).
 * - Mobile: Dialog fullScreen, sidebar vira tabs horizontais no topo.
 *
 * Sem dependencia de backend — conteudo 100% estatico de ./content.js.
 */
const AiToolsGuideModal = function (props) {
  var open = props.open || false;
  var onClose = props.onClose || function () {};
  var initialId = props.initialId || (AI_TOOLS_GUIDE[0] && AI_TOOLS_GUIDE[0].id);

  var theme = useTheme();
  var isMobile = useMediaQuery(theme.breakpoints.down("xs"));

  var [selectedId, setSelectedId] = useState(initialId);

  // Re-sincroniza quando o caller mudar `initialId` (ex: futuro deep-link)
  React.useEffect(function () {
    if (open && props.initialId) setSelectedId(props.initialId);
  }, [open, props.initialId]);

  var selectedTool = AI_TOOLS_GUIDE.find(function (t) {
    return t.id === selectedId;
  }) || AI_TOOLS_GUIDE[0];

  var dialogPaperStyle = {
    background: "#0f1115",
    border: "1px solid rgba(255,255,255,0.08)",
    color: "rgba(255,255,255,0.88)",
    maxWidth: 880,
    width: "100%"
  };

  var headerStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 18px",
    borderBottom: "1px solid rgba(255,255,255,0.08)"
  };

  var titleStyle = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontSize: 15,
    fontWeight: 600
  };

  var bodyStyle = isMobile
    ? {
        display: "flex",
        flexDirection: "column",
        padding: 0,
        height: "100%",
        overflow: "hidden"
      }
    : {
        display: "flex",
        flexDirection: "row",
        padding: 0,
        minHeight: 480,
        maxHeight: "70vh"
      };

  // SIDEBAR (desktop) ou TABS (mobile)
  var sidebarStyle = isMobile
    ? {
        display: "flex",
        flexDirection: "row",
        overflowX: "auto",
        borderBottom: "1px solid rgba(255,255,255,0.08)",
        padding: "8px 8px",
        gap: 6,
        flexShrink: 0
      }
    : {
        width: 240,
        borderRight: "1px solid rgba(255,255,255,0.08)",
        padding: "12px 8px",
        overflowY: "auto",
        flexShrink: 0
      };

  var renderSidebarItem = function (tool) {
    var Icon = tool.icon;
    var isSel = tool.id === selectedId;
    var baseStyle = isMobile
      ? {
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "8px 12px",
          borderRadius: 8,
          fontSize: 11,
          fontWeight: 600,
          whiteSpace: "nowrap",
          cursor: "pointer",
          flexShrink: 0,
          background: isSel ? "rgba(91,124,255,0.18)" : "transparent",
          border: isSel
            ? "1px solid rgba(91,124,255,0.35)"
            : "1px solid transparent",
          color: isSel ? "#fff" : "rgba(255,255,255,0.65)"
        }
      : {
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 12px",
          borderRadius: 8,
          fontSize: 12,
          fontWeight: 500,
          cursor: "pointer",
          marginBottom: 4,
          background: isSel ? "rgba(91,124,255,0.12)" : "transparent",
          border: isSel
            ? "1px solid rgba(91,124,255,0.28)"
            : "1px solid transparent",
          color: isSel ? "#fff" : "rgba(255,255,255,0.72)",
          transition: "background 0.15s"
        };
    return (
      <div
        key={tool.id}
        style={baseStyle}
        onClick={function () {
          setSelectedId(tool.id);
        }}
      >
        {Icon ? <Icon size={isMobile ? 12 : 14} /> : null}
        <span>{tool.title}</span>
      </div>
    );
  };

  // CONTEUDO da tool selecionada
  var contentStyle = {
    flex: 1,
    padding: isMobile ? "16px" : "20px 24px",
    overflowY: "auto"
  };

  var sectionTitleStyle = {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: "rgba(255,255,255,0.45)",
    marginBottom: 6,
    marginTop: 16
  };

  var paragraphStyle = {
    fontSize: 13,
    lineHeight: 1.55,
    color: "rgba(255,255,255,0.82)"
  };

  var listStyle = {
    margin: "4px 0 0 0",
    paddingLeft: 18,
    fontSize: 12,
    lineHeight: 1.6,
    color: "rgba(255,255,255,0.78)"
  };

  var exampleWrapStyle = {
    marginTop: 8,
    padding: "12px 12px 4px",
    borderRadius: 10,
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.06)"
  };

  var SelectedIcon = selectedTool.icon;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth={false}
      PaperProps={{ style: dialogPaperStyle }}
    >
      <div style={headerStyle}>
        <div style={titleStyle}>
          <BookOpen size={16} />
          Guia das automações da IA
        </div>
        <IconButton size="small" onClick={onClose} style={{ color: "rgba(255,255,255,0.7)" }}>
          <X size={18} />
        </IconButton>
      </div>

      <DialogContent style={{ padding: 0, overflow: "hidden" }}>
        <div style={bodyStyle}>
          <div style={sidebarStyle}>
            {AI_TOOLS_GUIDE.map(renderSidebarItem)}
          </div>

          <div style={contentStyle}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
              {SelectedIcon ? <SelectedIcon size={18} color="#5B7CFF" /> : null}
              <div style={{ fontSize: 15, fontWeight: 600, color: "#fff" }}>
                {selectedTool.title}
              </div>
            </div>

            <div style={sectionTitleStyle}>O que faz</div>
            <div style={paragraphStyle}>{selectedTool.whatItDoes}</div>

            <div style={sectionTitleStyle}>Quando usar</div>
            <ul style={listStyle}>
              {(selectedTool.whenToUse || []).map(function (item, idx) {
                return <li key={idx}>{item}</li>;
              })}
            </ul>

            <div style={sectionTitleStyle}>Exemplo de conversa</div>
            <div style={exampleWrapStyle}>
              {(selectedTool.example || []).map(function (b, idx) {
                return (
                  <ChatBubble
                    key={idx}
                    side={b.side}
                    text={b.text}
                    effect={b.effect}
                  />
                );
              })}
            </div>

            {selectedTool.whenNotToUse && selectedTool.whenNotToUse.length > 0 ? (
              <React.Fragment>
                <div style={sectionTitleStyle}>Quando NÃO usar</div>
                <ul style={listStyle}>
                  {selectedTool.whenNotToUse.map(function (item, idx) {
                    return <li key={idx}>{item}</li>;
                  })}
                </ul>
              </React.Fragment>
            ) : null}
          </div>
        </div>
      </DialogContent>

      <DialogActions style={{ borderTop: "1px solid rgba(255,255,255,0.08)", padding: "10px 16px" }}>
        <Button onClick={onClose} style={{ color: "rgba(255,255,255,0.75)", textTransform: "none" }}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AiToolsGuideModal;
